// Per-facility white-labeling. Loads branding for the logged-in user's
// facility from the backend, exposes it via context, and applies the theme
// colors as CSS custom properties on the document root so any component can
// use --brand-primary / --brand-accent in inline styles or tailwind arbitrary
// values like className="text-[var(--brand-primary)]".

import { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api.js';
import { useAuth } from './auth.jsx';

const DEFAULT = {
  display_name: 'Meds-to-Beds',
  logo_url: null,
  theme_primary: '#1d4ed8', // blue-700
  theme_accent:  '#0ea5e9', // sky-500
};

const BrandingCtx = createContext({ branding: DEFAULT, refresh: () => {}, applyPreview: () => {} });

function applyTheme(b) {
  if (typeof document === 'undefined') return;
  const r = document.documentElement;
  r.style.setProperty('--brand-primary', b.theme_primary || DEFAULT.theme_primary);
  r.style.setProperty('--brand-accent',  b.theme_accent  || DEFAULT.theme_accent);
  if (b.display_name) document.title = `${b.display_name} · Meds-to-Beds`;
}

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULT);

  async function refresh() {
    try {
      const r = await api.branding();
      const b = { ...DEFAULT, ...r.branding };
      setBranding(b);
      applyTheme(b);
    } catch {
      applyTheme(DEFAULT);
    }
  }

  // Used by the Branding admin screen for live preview without saving.
  function applyPreview(partial) {
    const merged = { ...branding, ...partial };
    setBranding(merged);
    applyTheme(merged);
  }

  useEffect(() => {
    if (user) refresh();
    else applyTheme(DEFAULT);
  }, [user?.facility_id]);

  return (
    <BrandingCtx.Provider value={{ branding, refresh, applyPreview }}>
      {children}
    </BrandingCtx.Provider>
  );
}

export const useBranding = () => useContext(BrandingCtx);
